import stringify from 'json-stable-stringify';

import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {forceFetchManagedModel} from 'models/ManagedModelUtil';
import StringResolutionManager from 'models/managers/StringResolutionManager';
import {
  SampleLookupInfo,
  StructuredTable,
  StructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import fetchAllSamples from 'notebook/structured-tables/util/fetchAllSamples';
import {ResolutionQuery} from 'notebook/tables/excess/Resolver';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import * as Perf from 'performance/Perf';
import {FETCH_SAMPLES_FINISHED_EVENT, FETCH_SAMPLES_STARTED_EVENT} from 'performance/processNotebookEvents';
import APIUtil from 'util/APIUtil';

export const FETCH_STRUCTURED_TABLE_LIVE_DATA_PERF_INTERVAL = 60 * 1000;

const fetchStructuredTableLiveDataPerfId =
  Perf.start(
    `fetch_structured_table_live_data`,
    {enabled: true, getMetadata: () => ({})},
    {
      interval: FETCH_STRUCTURED_TABLE_LIVE_DATA_PERF_INTERVAL,
      aggregations: [Perf.Aggregation.COUNT, Perf.Aggregation.MEDIAN, Perf.Aggregation.MAX],
    }
  ) ?? '';

/**
 * Fetch live data backing up the evaluation structured table required for surfacing stale indicators.
 *
 * As of now, we only perform live lookup column queries. This function is called only on entry mount.
 */
export async function fetchLiveDataForStaleEvaluation(
  evaluatedCellResolutionQueries: Array<ResolutionQuery>
): Promise<void> {
  // TODO(BNCH-32523): We should hook listeners into Redux directly instead of blocking on `fetchMulti`.
  await StringResolutionManager.fetchMulti(
    evaluatedCellResolutionQueries
      .filter(
        (query) =>
          query.type === ResolutionQueryType.CUSTOM &&
          query.customResolutionQueryType === CustomResolutionQueryType.LOOKUP_COLUMN
      )
      .map((query) => stringify(query))
  );
}

/**
 * Fetch live data backing up the raw data in the structured table required for surfacing stale indicators.
 *
 * Because this function is called for every table in an entry, we want to evaluate how performant it is.
 */
export async function fetchStructuredTableLiveData<TTableMetadata, TRowMetadata>(
  adapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>,
  primarySampleLookupTypes: Array<SampleLookupInfo>,
  structuredTable?: StructuredTable<TTableMetadata, TRowMetadata>
): Promise<void> {
  Perf.markStart(fetchStructuredTableLiveDataPerfId);
  primarySampleLookupTypes.forEach((sampleLookupInfo) => {
    // TODO(BNCH-18978): Remove once we have implemented a RowUpdater for the Registration table
    if (APIUtil.isBiologicalEntity(sampleLookupInfo.idForLookup)) {
      forceFetchManagedModel(ManagedModelType.BIOENTITY_SUMMARY, sampleLookupInfo.idForLookup);
    }
  });

  // We also want to let the StructuredTableRowUpdater fetch any data that they need for each of the models
  const samples = primarySampleLookupTypes.map((sampleLookupInfo) => ({
    sampleLookupInfo,
    columnGroup: null,
    schemaId: null,
  }));
  logProfilerTimingEvent(FETCH_SAMPLES_STARTED_EVENT, {
    sampleCount: samples.length,
    tableKind: structuredTable?.kind,
    isUsingBulkQueries: true,
  });
  await fetchAllSamples(samples, adapter.getAllRowUpdaters(structuredTable?.columns));
  logProfilerTimingEvent(FETCH_SAMPLES_FINISHED_EVENT);
  Perf.markEnd(fetchStructuredTableLiveDataPerfId);
}
